// client/src/components/ui/ProjectionChart.jsx
import React from "react";

export default function ProjectionChart({ data, title, color = "#6366f1" }) {
  if (!data || data.length === 0) return null;

  const maxValue = Math.max(...data.map(d => Math.max(d.stress, d.turnover, d.productivity)));
  const height = 200;
  const width = 500;
  const padding = 40;

  const xScale = (index) => padding + (index / (data.length - 1)) * (width - 2 * padding);
  const yScale = (value) => height - padding - ((value / maxValue) * (height - 2 * padding));

  const createPath = (values) => {
    return values
      .map((value, i) => {
        const x = xScale(i);
        const y = yScale(value);
        return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
      })
      .join(" ");
  };

  const stressPath = createPath(data.map(d => d.stress));
  const turnoverPath = createPath(data.map(d => d.turnover));
  const productivityPath = createPath(data.map(d => d.productivity));

  return (
    <div className="bg-black/40 rounded-lg p-4">
      <div className="text-sm font-semibold text-white mb-3">{title}</div>

      <svg width={width} height={height} className="w-full">
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map(value => (
          <g key={value}>
            <line
              x1={padding}
              y1={yScale(value)}
              x2={width - padding}
              y2={yScale(value)}
              stroke="rgba(100, 116, 139, 0.2)"
              strokeWidth="1"
            />
            <text
              x={padding - 10}
              y={yScale(value)}
              fill="#94a3b8"
              fontSize="10"
              textAnchor="end"
              dominantBaseline="middle"
            >
              {value}
            </text>
          </g>
        ))}

        {/* X-axis labels */}
        {data.map((d, i) => (
          <text
            key={i}
            x={xScale(i)}
            y={height - 20}
            fill="#94a3b8"
            fontSize="10"
            textAnchor="middle"
          >
            {d.month}
          </text>
        ))}

        {/* Lines */}
        <path
          d={stressPath}
          fill="none"
          stroke="#ef4444"
          strokeWidth="2"
          opacity="0.8"
        />
        <path
          d={turnoverPath}
          fill="none"
          stroke="#f59e0b"
          strokeWidth="2"
          opacity="0.8"
        />
        <path
          d={productivityPath}
          fill="none"
          stroke="#10b981"
          strokeWidth="3"
          opacity="0.9"
        />

        {/* Data points */}
        {data.map((d, i) => (
          <g key={i}>
            <circle cx={xScale(i)} cy={yScale(d.stress)} r="3" fill="#ef4444" />
            <circle cx={xScale(i)} cy={yScale(d.turnover)} r="3" fill="#f59e0b" />
            <circle cx={xScale(i)} cy={yScale(d.productivity)} r="4" fill="#10b981" />
          </g>
        ))}
      </svg>

      {/* Legend */}
      <div className="flex gap-4 mt-3 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span className="text-slate-400">Stress</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <span className="text-slate-400">Turnover</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500" />
          <span className="text-slate-400">Productivité</span>
        </div>
      </div>
    </div>
  );
}